import {
  Table,
  Stack,
  Dialog,
  Button,
  Divider,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
} from '@mui/material';
import { fNumber } from 'src/utils/format-number';
import { fDate } from 'src/utils/format-time';
import InfoGrid from 'src/components/ui-kit/info-grid';
import { RedeemTxType } from 'src/type/gold-withdraw';
import FormProvider, { RHFTextField } from 'src/components/hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import { toastError } from 'src/utils/format-error';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import axios, { endpoints } from 'src/utils/axios';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  tx: RedeemTxType;
  handleReload: VoidFunction;
};

export default function RedeemUpdateDialog({ open, onClose, tx, handleReload }: Props) {
  const getInfo = () => {
    if (!tx) return [];

    const { appointmentDate, newAppointmentDate, note } = tx;

    return [
      {
        label: 'วันที่นัดหมาย (เดิม)',
        value: fDate(appointmentDate, 'DD MMMM BBBB HH:mm น.') || '-',
      },
      {
        label: 'วันที่นัดหมาย (ใหม่)',
        value: fDate(newAppointmentDate, 'DD MMMM BBBB HH:mm น.') || '-',
      },
      {
        label: 'หมายเหตุ',
        value: note || '-',
      },
    ];
  };

  const Schema = Yup.object().shape({
    note: Yup.string(),
    newAppointmentDate: Yup.mixed<Dayjs>().required('กรุณาเลือกวันนัดหมาย'),
    newAppointmentTime: Yup.mixed<Dayjs>().required('กรุณาเลือกเวลานัดหมาย'),
  });

  const defaultValues = {
    note: '',
    newAppointmentDate: null as unknown as Dayjs,
    newAppointmentTime: null as unknown as Dayjs,
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    control,
    getValues,
    formState: { isSubmitting },
  } = methods;

  const beforeClose = () => {
    reset(defaultValues);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      const combinedDateTime = dayjs(values.newAppointmentDate)
        .set('hour', dayjs(values.newAppointmentTime).hour())
        .set('minute', dayjs(values.newAppointmentTime).minute())
        .set('second', dayjs(values.newAppointmentTime).second());

      await axios.patch(endpoints.transactions.updateNewAppointmentDate(tx.id), {
        newAppointmentDate: combinedDateTime.toISOString(),
        ...(values.note && { note: values.note }),
      });

      handleReload();
      beforeClose();
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      toastError(error);
    }
  });

  // console.log(tx?.productRedeemFees);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle sx={{ pb: 2, display: 'flex' }}>
          เปลี่ยนวันนัดหมายการถอนสินค้า
          {tx.product.name} {tx.amount} {tx.unitName || 'บาท'}
        </DialogTitle>

        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell align="center">จำนวน</TableCell>
                  <TableCell align="center">ค่าธรรมเนียม</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {tx?.productRedeemFees?.map((fee) => (
                  <TableRow key={fee.id}>
                    <TableCell>{fee?.productRedeemFee?.name}</TableCell>
                    <TableCell align="center">{fNumber(fee?.amount || 0)}</TableCell>
                    <TableCell align="center">{fNumber(fee?.feeTotal || 0)} THB</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Divider style={{ borderStyle: 'dashed' }} />

            <Table size="small" sx={{ mt: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell>น้ำหนักทองทั้งหมด</TableCell>
                  <TableCell align="center">
                    {fNumber(tx?.amount || 0)} {tx.unitName || 'บาท'}
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>ค่าธรรมเนียมทั้งหมด</TableCell>
                  <TableCell align="center">{fNumber(tx?.fee || 0)} THB</TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
          <InfoGrid infos={getInfo()} />
          <Stack spacing={3}>
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Controller
                name="newAppointmentDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    label="เลือกวันนัดหมายใหม่"
                    value={getValues('newAppointmentDate')}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                    }}
                    disablePast
                    sx={{ width: 1 }}
                    format="DD/MM/YYYY"
                  />
                )}
              />

              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
                <Controller
                  name="newAppointmentTime"
                  control={control}
                  render={({ field }) => (
                    <TimePicker
                      {...field}
                      label="เลือกเวลานัดหมายใหม่"
                      value={getValues('newAppointmentTime')}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      sx={{ width: 1 }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Stack>

            <RHFTextField
              multiline
              rows={3}
              name="note"
              placeholder="หมายเหตุ"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            บันทึก
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
